import type { StockArticleInfo } from '../hooks/use-stock-info'
import { parseStockAmount } from '@/shared/lib/stock-quantity'

/** One line of the movement modal as typed. */
export interface MovementLineInput {
  product: { sku: string; name: string } | null
  quantity: string
}

export type PreparedMovementLines =
  | { ok: true; lines: Array<{ sku: string; quantity: number }> }
  /** A dish, variant or not-tracked article: the server would refuse it (product_has_no_stock). */
  | { ok: false; error: 'noStock'; name: string }
  /** A partial-amount line whose text is not a quantity with at most 3 decimals. */
  | { ok: false; error: 'invalidNumber' }

/**
 * The request lines of a manual movement (CHG-2026-09-stock-usage-and-price-twins, D9, D11).
 *
 * A whole-number article — and any article whose counting mode is not known — keeps exactly the
 * conversion the modal always used (`Number(text)`, lines that are not finite dropped). Only an
 * article with "Teilmengen zählen" reads the de-AT comma, and sends the typed value.
 */
export function prepareMovementLines(
  lines: Array<MovementLineInput>,
  stockInfo: Map<string, StockArticleInfo>,
): PreparedMovementLines {
  const refused = lines.find(
    (l) =>
      l.product !== null &&
      stockInfo.get(l.product.sku.trim())?.noStock != null,
  )
  if (refused?.product) {
    return { ok: false, error: 'noStock', name: refused.product.name }
  }
  const prepared: Array<{ sku: string; quantity: number }> = []
  for (const line of lines) {
    if (line.product === null) continue
    const sku = line.product.sku.trim()
    let quantity: number
    if (stockInfo.get(sku)?.fractional) {
      const parsed = parseStockAmount(line.quantity)
      if (!parsed.ok) return { ok: false, error: 'invalidNumber' }
      quantity = parsed.value
    } else {
      quantity = Number(line.quantity)
    }
    if (sku && Number.isFinite(quantity)) prepared.push({ sku, quantity })
  }
  return { ok: true, lines: prepared }
}
